import 'views/theme.less'

import {Icon} from '@codeurs/front'
import React, {HTMLProps} from 'react'
import {LinkType} from 'types'
import {createTheme} from 'util/createtheme'
import {Link as UtilLink, LinkProps} from 'util/link'
import {Mod, style} from 'util/style'
import {Trusted} from 'util/trusted'

type Div = HTMLProps<HTMLDivElement>
type Span = HTMLProps<HTMLSpanElement>
const appEnv: string = (window as any).__APP_ENV__

const styles = style('theme', [
	'outercontainer',
	'container',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'p',
	'wysiwyg',
	'button',
	'buttontext',
	'buttonicon',
	'buttonsmap',
	'link',
	'linktext',
	'linkicon',
	'linksmap'
])

const OuterContainer: React.FC<HTMLProps<HTMLDivElement>> = ({...props}) => (
	<div {...props} className={styles.outercontainer().mergeProps(props)} />
)

const Container: React.FC<HTMLProps<HTMLDivElement>> = ({
	children,
	...props
}) => {
	return (
		<div {...props} className={styles.container().mergeProps(props)}>
			{children}
			{/*appEnv === 'local' && (
				<Grid className={styles.container('grid').toString()}>
					{Array.from(Array(12)).map((_, i) => (
						<Column
							className={styles.container('grid-item').toString()}
							key={i}
						/>
					))}
				</Grid>
					)*/}
		</div>
	)
}

type Header = React.FC<
	{
		as?: any
		mod?: Mod<'light' | 'line' | 'dots'>
	} & HTMLProps<HTMLHeadingElement>
>

const H1: Header = ({as: Tag = 'h1', mod, children, ...props}) => (
	<div {...props}>
		<Tag {...props} className={styles.h1().mod(mod)}>
			{children}
		</Tag>
	</div>
)
const H2: Header = ({as: Tag = 'h2', mod, children, ...props}) => (
	<div {...props}>
		<Tag className={styles.h2().mod(mod)}>{children}</Tag>
	</div>
)
const H3: Header = ({as: Tag = 'h3', mod, children, ...props}) => (
	<div {...props}>
		<Tag className={styles.h3().mod(mod)}>{children}</Tag>
	</div>
)
const H4: Header = ({as: Tag = 'h4', mod, children, ...props}) => (
	<div {...props}>
		<Tag className={styles.h4().mod(mod)}>{children}</Tag>
	</div>
)
const H5: Header = ({as: Tag = 'h5', mod, children, ...props}) => (
	<div {...props}>
		<Tag className={styles.h5().mod(mod)}>{children}</Tag>
	</div>
)

const P: React.FC<HTMLProps<HTMLParagraphElement>> = props => (
	<p {...props} className={styles.p().mergeProps(props)} />
)

const Wysiwyg: React.FC<{
	children: string
	mod?: Mod<'inherit'>
} & any> = props => (
	<Trusted
		{...props}
		className={styles.wysiwyg.mod(props.mod).mergeProps(props)}
	/>
)

export type ButtonModType = {
	mod?: Mod<
		| ''
		| 'light'
		| 'outline'
		| 'rounded'
		| 'round'
		| 'purple'
		| 'white'
		| 'gradient'
		| 'arrowlong'
		| 'inactive'
		| 'submitting'
		| 'uppercase'
	>
}

const Button: React.FC<LinkProps & ButtonModType & {as?: 'button'}> = ({
	as,
	mod,
	children,
	...props
}) => {
	const ButtonTag: any = as || UtilLink

	return (
		<ButtonTag
			{...props}
			className={styles
				.button()
				.mod(mod)
				.mergeProps(props)}
		>
			{children}
		</ButtonTag>
	)
}

const ButtonText: React.FC<Span> = props => (
	<span {...props} className={styles.buttontext().mergeProps(props)} />
)

const ButtonIcon: React.FC<{icon: string} & Span> = ({icon, ...props}) => (
	<span {...props} className={styles.buttonicon().mergeProps(props)}>
		<Icon icon={icon} />
	</span>
)

type LinkModType = {
	mod?: 'arrowlong' | 'inherit'
}

const ButtonsMap: React.FC<{links?: Array<LinkType>} & Div> = ({
	links,
	...props
}) => {
	if (!links || links.length === 0) return

	return (
		<div className={styles.buttonsmap().mergeProps(props)}>
			{links.map((link, i) => (
				<div key={i} className={styles.buttonsmap('item')}>
					<Theme.Button
						to={link.url}
						type={link.type}
						target={link.target}
						mod={['gradient', 'uppercase']}
					>
						<Theme.ButtonText>
							{link.description || link.title}
						</Theme.ButtonText>
						<Theme.ButtonIcon icon="arrow-long" />
					</Theme.Button>
				</div>
			))}
		</div>
	)
}

const Link: React.FC<LinkProps & LinkModType> = ({mod, children, ...props}) => {
	return (
		<UtilLink
			{...props}
			className={styles
				.link()
				.mod(mod)
				.mergeProps(props)}
		>
			{children}
		</UtilLink>
	)
}

const LinkText: React.FC<Span> = props => (
	<span {...props} className={styles.linktext().mergeProps(props)} />
)

const LinkIcon: React.FC<{icon: string} & Span> = ({icon, ...props}) => (
	<span {...props} className={styles.linkicon().mergeProps(props)}>
		<Icon icon={icon} />
	</span>
)

const LinksMap: React.FC<{links?: Array<LinkType>} & Div> = ({
	links,
	...props
}) => {
	if (!links || links.length === 0) return

	return (
		<div className={styles.linksmap().mergeProps(props)}>
			{links.map((link, i) => (
				<div key={i} className={styles.linksmap('item')}>
					<Theme.Link
						to={link.url}
						type={link.type}
						target={link.target}
						mod="arrowlong"
					>
						<Theme.LinkText>{link.description || link.title}</Theme.LinkText>
						<Theme.LinkIcon icon="arrow-long" />
					</Theme.Link>
				</div>
			))}
		</div>
	)
}

export const defaultTheme = {
	OuterContainer,
	Container,
	H1,
	H2,
	H3,
	H4,
	H5,
	P,
	Wysiwyg,
	Button,
	ButtonText,
	ButtonIcon,
	ButtonsMap,
	Link,
	LinkText,
	LinkIcon,
	LinksMap
}

export const Theme = createTheme(defaultTheme)
